import { createFileRoute } from "@tanstack/react-router"

import { pageHead } from "@/lib/metadata"
import { StatusError } from "@/components/status/status-error"
import { getStatus } from "@/server/web"
import { ChevronDown } from "lucide-react"

import { StatusLabel } from "@anysola/ui/status-label"
import { Container } from "@/components/layout/container"
import { SiteShell } from "@/components/layout/site-shell"
import { IncidentStatusBadge } from "@/components/incident-status-badge"
import { IncidentTimeline } from "@/components/status/incident-timeline"
import { StatusSubscribeWidget } from "@/components/status/status-subscribe-widget"
import { UnavailableAlert } from "@/components/feedback/unavailable-alert"
import { RelativeTime } from "@anysola/ui/relative-time"
import { RowGroup } from "@anysola/ui/row-group"
import { summaryResetClass } from "@/lib/ui-classes"
import { cn } from "@anysola/ui/utils"
import { formatAgo } from "@anysola/contracts/shared/relative-time"
import {
  incidentBannerStatus,
  type PublicIncident,
} from "@anysola/contracts/web/incident-core"
import type { ServiceStatus } from "@anysola/contracts/web/service-status-core"

const summaryClass = cn(
  summaryResetClass,
  "flex items-center gap-4 bg-card px-4 py-3 transition-colors hover:bg-surface-secondary focus-visible:bg-surface-secondary sm:px-5"
)

function Chevron() {
  return (
    <ChevronDown
      aria-hidden
      className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
    />
  )
}

/** "Down for 3h" within the week (§3's relative window), "Down since
 *  16 Oct" after it, "Just went down" in its first minute. */
function downFor(since: Date) {
  const ago = formatAgo(since)
  if (ago === "now") return "Just went down"
  return ago.endsWith(" ago")
    ? `Down for ${ago.slice(0, -" ago".length)}`
    : `Down since ${ago}`
}

/** One service as a row that folds open to the endpoints checked under it. */
function ServiceRow({ service }: { service: ServiceStatus }) {
  const state = service.down ? (
    <StatusLabel tone="warning">
      {service.since ? (
        <RelativeTime value={service.since} format={downFor} />
      ) : (
        "Down"
      )}
    </StatusLabel>
  ) : (
    <StatusLabel tone="success">Operational</StatusLabel>
  )
  const parts = service.parts ?? []
  if (!parts.length) {
    return (
      <li className={cn(summaryClass, "min-h-16 cursor-default hover:bg-card")}>
        <ServiceName service={service} />
        {state}
        <span className="size-4 shrink-0" />
      </li>
    )
  }
  return (
    <li>
      <details className="group">
        <summary className={cn(summaryClass, "min-h-16")}>
          <ServiceName service={service} />
          {state}
          <Chevron />
        </summary>
        <ul className="mt-0.5 flex flex-col gap-0.5">
          {parts.map((part) => (
            <li
              key={part.name}
              className="flex min-h-11 items-center gap-4 bg-card/60 py-2 pr-13 pl-8 sm:pl-9"
            >
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground">
                {part.name}
              </span>
              <StatusLabel tone={part.down ? "warning" : "success"}>
                {part.down ? "Down" : "Operational"}
              </StatusLabel>
            </li>
          ))}
        </ul>
      </details>
    </li>
  )
}

function ServiceName({ service }: { service: ServiceStatus }) {
  return (
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[0.9375rem] text-foreground">
        {service.name}
      </span>
      <span className="block truncate text-xs text-muted-foreground">
        {service.host}
      </span>
    </span>
  )
}

/** An open incident: its name, one state, and every update under it. */
function OpenIncident({ incident }: { incident: PublicIncident }) {
  return (
    <section
      aria-labelledby={`incident-${incident._id}`}
      className="rounded-surface bg-card p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
        <h2
          id={`incident-${incident._id}`}
          className="text-lg font-medium tracking-tight text-foreground"
        >
          {incident.title}
        </h2>
        <span className="flex items-center gap-3 text-xs text-muted-foreground">
          <IncidentStatusBadge status={incident.status} />
          <span>
            Started <RelativeTime value={incident.createdAt} />
          </span>
        </span>
      </div>
      <div className="mt-5 rounded-xl bg-background p-4 sm:p-5">
        <IncidentTimeline updates={incident.updates} />
      </div>
    </section>
  )
}

/** A resolved incident folded to its title and last word. */
function PastIncident({ incident }: { incident: PublicIncident }) {
  const latest = incident.updates.at(-1)
  const at = incident.resolvedAt ?? latest?.createdAt ?? incident.createdAt
  return (
    <li>
      <details className="group">
        <summary className={cn(summaryClass, "items-start py-4")}>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-foreground">
              {incident.title}
            </span>
            {latest && (
              <span className="mt-0.5 line-clamp-1 block text-xs break-words text-muted-foreground">
                {latest.message}
              </span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-3 pt-0.5">
            <RelativeTime
              value={at}
              className="hidden text-xs text-muted-foreground sm:block"
            />
            <IncidentStatusBadge status={incident.status} />
            <Chevron />
          </span>
        </summary>
        <div className="mt-0.5 bg-card/60 px-4 py-4 sm:px-5">
          <IncidentTimeline updates={incident.updates} />
        </div>
      </details>
    </li>
  )
}

function StatusPage() {
  const { incidents, services, checkedAt } = Route.useLoaderData()
  const unreadable = incidents === null && services === null
  const active =
    incidents?.filter((incident) => incident.status !== "resolved") ?? []
  const past =
    incidents?.filter((incident) => incident.status === "resolved") ?? []
  const disrupted =
    (incidents !== null && incidentBannerStatus(incidents) === "disrupted") ||
    Boolean(services?.some((service) => service.down))
  // Checks unread and no incident to report: not "operational", just unknown.
  const unknown = !disrupted && services === null

  return (
    <SiteShell>
      <h1 className="sr-only">System status</h1>

      <Container className="grid gap-10 pt-10 pb-16 sm:pt-14 sm:pb-24 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
        <div className="min-w-0 space-y-10">
          {unreadable ? (
            // One alert for the whole page: neither read worked.
            <UnavailableAlert title="Status is unavailable right now." />
          ) : (
            <>
              <section aria-labelledby="services" className="space-y-0.5">
                {/* The answer heads the services card: the line people came for. */}
                <div
                  role="status"
                  className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded-t-surface bg-card px-4 py-5 sm:px-5"
                >
                  <h2
                    id="services"
                    className="flex items-center gap-3 text-xl font-medium tracking-tight text-foreground sm:text-2xl"
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "size-2.5 shrink-0 rounded-full",
                        disrupted
                          ? "bg-warning"
                          : unknown
                            ? "bg-muted-foreground"
                            : "bg-success"
                      )}
                    />
                    {disrupted
                      ? "We’re aware of an issue"
                      : unknown
                        ? "Status unknown"
                        : "All systems operational"}
                  </h2>
                  {!unknown && (
                    <span className="text-xs text-muted-foreground">
                      Checked <RelativeTime value={checkedAt} /> · every 5
                      minutes
                    </span>
                  )}
                </div>
                {services === null ? (
                  <div className="rounded-b-surface bg-card p-4 sm:p-5">
                    <UnavailableAlert title="Service checks are unavailable right now." />
                  </div>
                ) : (
                  <RowGroup className="rounded-t-none">
                    {services.map((service) => (
                      <ServiceRow key={service.host} service={service} />
                    ))}
                  </RowGroup>
                )}
              </section>

              {active.map((incident) => (
                <OpenIncident key={incident._id} incident={incident} />
              ))}

              <section aria-labelledby="history">
                <div className="mb-3 flex items-baseline justify-between gap-4 px-1">
                  <h2
                    id="history"
                    className="text-sm font-medium text-foreground"
                  >
                    Past incidents
                  </h2>
                  {past.length > 0 && (
                    <span className="text-sm text-muted-foreground tabular-nums">
                      {past.length}
                    </span>
                  )}
                </div>
                {incidents === null ? (
                  <UnavailableAlert title="Incidents couldn’t be loaded right now." />
                ) : past.length === 0 ? (
                  <p className="rounded-surface bg-card px-5 py-5 text-sm text-muted-foreground">
                    No incidents reported.
                  </p>
                ) : (
                  <RowGroup>
                    {past.map((incident) => (
                      <PastIncident key={incident._id} incident={incident} />
                    ))}
                  </RowGroup>
                )}
              </section>
            </>
          )}
        </div>

        <aside className="lg:sticky lg:top-20">
          <StatusSubscribeWidget />
        </aside>
      </Container>
    </SiteShell>
  )
}

export const Route = createFileRoute("/status/")({
  // Read again at most every 30 s (the service), the Next page's `revalidate`.
  loader: () => getStatus(),
  head: () =>
    pageHead({
      title: "Status",
      description: "Live status and incident history for Anysola.",
      path: "/status",
      image: "/status/opengraph-image",
    }),
  component: StatusPage,
  errorComponent: StatusError,
})
